"use client";

import PillButton from "@/app/shared/PillButton";
import { Alert, AlertTitle, Divider, Typography } from "@mui/material";
import Box from "@mui/material/Box";
import FacebookRoundedIcon from "@mui/icons-material/FacebookRounded";
import GoogleIcon from "@mui/icons-material/Google";
import { signIn } from "next-auth/react";
import { ReactNode, useState } from "react";
import { useAuthSearchParams } from "./useAuthSearchParams";
import EmailLookupForm from "./EmailLookupForm";

function ContinueAuthButton({
  icon,
  provider,
  onClick,
}: {
  icon: ReactNode;
  provider: string;
  onClick?: () => void;
}) {
  return (
    <PillButton startIcon={icon} onClick={onClick}>
      {provider}
    </PillButton>
  );
}

type AuthStep = "landing" | "lookup";

export default function AuthPage() {
  const [step, setStep] = useState<AuthStep>("landing");
  const { returnUrl, error } = useAuthSearchParams();

  const handleSignIn = (provider: string) => {
    signIn(provider, {
      redirectTo: returnUrl,
    });
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 4,
        alignItems: "center",
      }}
    >
      {step === "landing" && (
        <>
          <Typography variant="h5" sx={{ fontWeight: "bold" }}>
            Sign up or log in
          </Typography>
          <Typography>Continue with</Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              gap: 2,
              alignItems: "center",
            }}
          >
            <ContinueAuthButton
              icon={<GoogleIcon />}
              provider="google"
              onClick={() => handleSignIn("google")}
            />
            <ContinueAuthButton
              icon={<FacebookRoundedIcon />}
              provider="facebook"
              onClick={() => handleSignIn("facebook")}
            />
          </Box>
          <Divider flexItem>
            <Typography>or</Typography>
          </Divider>
        </>
      )}
      <EmailLookupForm
        onBack={() => setStep("landing")}
        onLookup={() => setStep("lookup")}
      />
      {error && (
        <Alert severity="error">
          <AlertTitle>{error.title}</AlertTitle>
          {error.body}
        </Alert>
      )}
    </Box>
  );
}
